import React from 'react';
import { ArrowRight, BadgePercent, CalendarDays, MapPin } from 'lucide-react';
import { formatOfferDate, getOfferCategoryKey } from '../../utils/offers';
import { InterestStatusBadge } from './InterestStatusBadge';

export const OfferCard = ({
  offer,
  locale,
  t,
  needsCompactFont,
  onViewDetails,
  compact = false,
  showStatus = true,
}) => {
  if (!offer) return null;

  return (
    <article
      className={`app-surface overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md ${
        compact ? 'h-full p-4' : 'h-full p-5'
      }`}
    >
      {offer.bannerImageUrl && !compact && (
        <img
          src={offer.bannerImageUrl}
          alt={offer.title}
          className="h-40 w-full rounded-2xl object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      )}

      <div className={compact ? 'space-y-3' : `space-y-4 ${offer.bannerImageUrl ? 'mt-4' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-300">
                {offer.brandName}
              </p>
              {offer.featured && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-1 text-[11px] font-semibold text-primary-700 dark:bg-primary-900/20 dark:text-primary-200">
                  <BadgePercent size={12} />
                  {t('offers.featuredBadge')}
                </span>
              )}
            </div>
            <h3
              className={`mt-2 break-words font-serif font-semibold tracking-tight text-primary-900 dark:text-white ${
                compact
                  ? needsCompactFont
                    ? 'text-[1rem] leading-7'
                    : 'text-lg leading-7'
                  : needsCompactFont
                    ? 'text-[1.08rem] leading-7'
                    : 'text-xl leading-8'
              }`}
            >
              {offer.title}
            </h3>
          </div>
          {showStatus && offer.interestAlreadySubmitted && offer.interestStatus && (
            <InterestStatusBadge status={offer.interestStatus} t={t} className="shrink-0" />
          )}
        </div>

        <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
          <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            {t(`offers.categories.${getOfferCategoryKey(offer.category)}`)}
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            {offer.offerType === 'UNIVERSAL' ? t('offers.universal') : t('offers.locationSpecific')}
          </span>
        </div>

        {offer.shortSummary && (
          <p className={`text-sm text-primary-700 dark:text-primary-200 ${needsCompactFont ? 'leading-7' : 'leading-6'}`}>
            {offer.shortSummary}
          </p>
        )}

        <div className="grid gap-2 text-sm text-primary-700 dark:text-primary-200 sm:grid-cols-2">
          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-primary-500" />
            <span>{offer.cityLabel || t('offers.availableAcrossIndia')}</span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarDays size={15} className="text-primary-500" />
            <span>
              {offer.validTill
                ? t('offers.validTillValue', { date: formatOfferDate(offer.validTill, locale) })
                : t('offers.validWhileActive')}
            </span>
          </div>
        </div>

        {offer.referenceId && (
          <div className="app-note bg-primary-50/80 text-primary-800 dark:bg-primary-900/20 dark:text-primary-100">
            <span className="font-semibold">{t('offers.referenceId')}:</span> {offer.referenceId}
          </div>
        )}

        <button
          type="button"
          onClick={() => onViewDetails?.(offer)}
          className="app-secondary-button w-full justify-center"
        >
          <span>{t('offers.viewDetails')}</span>
          <ArrowRight size={16} />
        </button>
      </div>
    </article>
  );
};

export default OfferCard;
